import { useQuery } from '@tanstack/react-query'
import { apiClient } from './client'

export interface GeoIPLookup {
  ip?: string
  city?: string | null
  region?: string | null
  region_code?: string | null
  country?: string | null
  country_code?: string | null
  continent?: string | null
  continent_code?: string | null
  latitude?: number | null
  longitude?: number | null
  timezone?: string | null
}

export interface GeoIPLookupResult {
  found: boolean
  lookup?: GeoIPLookup | null
  error?: string | null
  // Network operator of the IP from its registry (RDAP/whois), like
  // "Orange S.A." or "OVH SAS". Independent of `found`, which only covers
  // the location.
  provider?: string | null
  // CIDR block the provider record covers.
  network?: string | null
}

export type GeoIPMap = Record<string, GeoIPLookupResult>

// canonicalizeIP strips an optional :port suffix so values like
// "[::1]:52076" or "127.0.0.1:8080" become "::1" / "127.0.0.1".
// The backend keys results by the bare IP, so callers must use this
// helper both when building the request and when reading the response.
export function canonicalizeIP(ip: string): string {
  if (!ip) return ip
  if (ip.startsWith('[')) {
    const close = ip.indexOf(']')
    if (close > 0) return ip.slice(1, close)
    return ip
  }
  const first = ip.indexOf(':')
  const last = ip.lastIndexOf(':')
  if (first !== -1 && first === last) {
    return ip.slice(0, first)
  }
  return ip
}

// useGeoIPBulk resolves a deduplicated set of IP addresses via the bulk
// endpoint. Returns a map keyed by the canonical IP form.
export function useGeoIPBulk(ips: string[]) {
  // Strip ports, dedup, sort — the backend keys by bare IP and rejects
  // "ip:port" forms, so two sessions from "[::1]:1" and "[::1]:2" must
  // collapse to a single "::1" lookup.
  const key = Array.from(new Set(ips.map(canonicalizeIP))).sort()
  return useQuery<GeoIPMap>({
    queryKey: ['geoip', 'bulk', key],
    enabled: key.length > 0,
    staleTime: 1000 * 60 * 60, // 1h — backend cache is the real one.
    retry: 1,
    queryFn: async () => {
      const { data, error } = await apiClient.POST('/api/v1/geoip/lookup', {
        body: { ips: key },
      } as any)
      if (error) {
        throw new Error(typeof error === 'string' ? error : JSON.stringify(error))
      }
      return ((data as any)?.results ?? {}) as GeoIPMap
    },
  })
}

// formatLocation extracts a human-friendly label from a GeoIP result.
// Returns null when the result is missing, negative, or has no usable text —
// callers should fall back to the raw IP in that case.
export function formatLocation(result: GeoIPLookupResult | undefined): string | null {
  if (!result || !result.found || !result.lookup) return null
  const l = result.lookup
  const cc = l.country_code ?? undefined
  if (l.city && cc) return `${l.city}, ${cc}`
  if (l.region && cc) return `${l.region}, ${cc}`
  if (l.country) return l.country
  if (cc) return cc
  return null
}

// formatProvider returns the network operator of a GeoIP result, or null.
export function formatProvider(result: GeoIPLookupResult | undefined): string | null {
  const provider = result?.provider?.trim()
  return provider ? provider : null
}
